import apiClient from './apiClient';
import { Transaction, TransactionFilter } from '../types/transaction.types';

class AgencyTransactionService {
  /**
   * Récupère toutes les transactions d'une agence avec filtres optionnels
   * @param filters Filtres à appliquer (date, statut, type, etc.)
   */
  async getAll() {
    try {
      // Dans une implémentation réelle, on enverrait les filtres dans la requête
      const response = await apiClient.get('/v1/transactions/agency/all');
      return response;
    } catch (error) {
      console.error('Error fetching agency transactions:', error);
      
      // Pour le développement, simuler une réponse
      return {
        data: [] /*this.getMockTransactions(filters)*/
      };
    }
  }

  /**
   * Exporte les transactions vers un fichier Excel
   */
  async exportToExcel() {
    try {
      const response = await apiClient.get('/agency/transactions/export', { responseType: 'blob' });
      
      // Téléchargement du fichier
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return response;
    } catch (error) {
      console.error('Error exporting transactions:', error);
      throw error;
    }
  }

  /**
   * Récupère les détails d'une transaction
   * @param id ID de la transaction
   */
  async getById(id: number) {
    try {
      const response = await apiClient.get(`/agency/transactions/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching transaction with ID ${id}:`, error);
      
      // Pour le développement, simuler une réponse
      return {
        data: this.getMockTransactions().find(t => t.id === id) || null
      };
    }
  }

  /**
   * Génère des transactions fictives pour le développement
   */
  private getMockTransactions(filters: TransactionFilter = {}): Transaction[] {
    const transactions: Transaction[] = [
      {
        id: 1001,
        reference: 'TRX-20250510-001',
        amount: 550,
        currency: 'EUR',
        type: 'PACK_PURCHASE',
        status: 'COMPLETED',
        paymentMethod: 'CARD',
        description: 'Achat de pack Premium',
        createdAt: '2025-05-10T10:30:00Z',
        completedAt: '2025-05-10T10:35:00Z'
      },
      {
        id: 1002,
        reference: 'TRX-20250511-002',
        amount: 320,
        currency: 'EUR',
        type: 'VOUCHER_PURCHASE',
        status: 'PENDING',
        paymentMethod: 'BANK_TRANSFER',
        description: 'Achat de vouchers (10)',
        createdAt: '2025-05-11T14:45:00Z'
      },
      {
        id: 1003,
        reference: 'TRX-20250508-003',
        amount: 150,
        currency: 'EUR',
        type: 'SUBSCRIPTION',
        status: 'COMPLETED',
        paymentMethod: 'CARD',
        description: 'Abonnement mensuel',
        createdAt: '2025-05-08T09:20:00Z',
        completedAt: '2025-05-08T09:22:00Z'
      },
      {
        id: 1004,
        reference: 'TRX-20250505-004',
        amount: 780,
        currency: 'EUR',
        type: 'PACK_PURCHASE',
        status: 'FAILED',
        paymentMethod: 'CARD',
        description: 'Achat de pack Business',
        createdAt: '2025-05-05T16:10:00Z',
        failedAt: '2025-05-05T16:12:00Z',
        failureReason: 'Carte refusée'
      },
      {
        id: 1005,
        reference: 'TRX-20250501-005',
        amount: 230,
        currency: 'EUR',
        type: 'VOUCHER_PURCHASE',
        status: 'COMPLETED',
        paymentMethod: 'CASH',
        description: 'Achat de vouchers (5)',
        createdAt: '2025-05-01T11:30:00Z',
        completedAt: '2025-05-01T11:35:00Z'
      }
    ];

    // Appliquer les filtres si nécessaire
    let filteredTransactions = [...transactions];
    
    if (filters.dateRange) {
      const start = new Date(filters.dateRange.start);
      const end = new Date(filters.dateRange.end);
      filteredTransactions = filteredTransactions.filter(t => {
        const date = new Date(t.createdAt);
        return date >= start && date <= end;
      });
    }

    if (filters.status) {
      filteredTransactions = filteredTransactions.filter(t => t.status === filters.status);
    }

    if (filters.type) {
      filteredTransactions = filteredTransactions.filter(t => t.type === filters.type);
    }

    return filteredTransactions;
  }
}

export const agencyTransactionService = new AgencyTransactionService();
